import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../../../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../../../components/ui/select';
import { Code, Settings, FileText, Info, ArrowLeft, Edit3 } from 'lucide-react';
import { Button } from '../../../../../../../components/ui/button';
import weldParametersData from './weldParameters.json';
import SoftwareVersions from './SoftwareVersions';
import ProjectInformation from './ProjectInformation';
import JobParameters from './JobParameters';
import PipeParametersTable from './PipeParametersTable';
import RenamePass from './RenamePass';

interface M500ParametersProps {
    jobData?: any[];
    projectId?: number;
    onRenameSuccess?: () => void;
    canEdit?: boolean;
}

interface JobParameter {
    job_number: string;
    [key: string]: any;
}

const M500Parameters: React.FC<M500ParametersProps> = ({ 
    jobData,
    projectId,
    onRenameSuccess,
    canEdit = true,
}) => {
    const [activeTab, setActiveTab] = useState<string>('software-versions');
    const [selectedJob, setSelectedJob] = useState<string>('');
    const [selectedPipeParam, setSelectedPipeParam] = useState<string>('');
    
    // Extract job numbers from the array
    const jobNumbers = useMemo(() => {
        if (!jobData || !Array.isArray(jobData)) return [];
        const numbers = jobData
            .map((item: JobParameter) => item?.job_number)
            .filter(Boolean)
            .map(num => String(num).trim());
        return numbers;
    }, [jobData]);

    // Set first job as default and reset when jobData changes
    useEffect(() => {
        if (jobNumbers.length > 0) {
            // If current selectedJob is not in the new jobNumbers, reset to first
            if (!selectedJob || !jobNumbers.includes(selectedJob)) {
                setSelectedJob(jobNumbers[0]);
            }
        } else {
            setSelectedJob('');
        }
    }, [jobNumbers, selectedJob]);

    // Get selected job data
    const selectedJobData = useMemo(() => {
        if (!selectedJob || !jobData) return null;
        const found = jobData.find((item: JobParameter) => {
            const itemJobNumber = String(item.job_number || '').trim();
            const selectedJobNumber = String(selectedJob || '').trim();
            return itemJobNumber === selectedJobNumber;
        });
        return found || null;
    }, [selectedJob, jobData]);

    const handleJobSelect = (jobNum: string) => {
        setSelectedJob(jobNum);
        // Reset pipe parameter when job changes
        setSelectedPipeParam('');
    };

    // Check if the job needs renaming (is_renamed = false)
    const needsRenaming = useMemo(() => {
        return true;
        if (!selectedJobData) return false;
        return selectedJobData.is_renamed === false;
    }, [selectedJobData]);

    // Extract available pipe parameter keys for selected job
    const pipeParameterKeys = useMemo(() => {
        if (!selectedJobData?.parsed_json_data) return [];
        
        const keys = Object.keys(selectedJobData.parsed_json_data)
            .filter(key => key.startsWith('PIPE PARAMETERS'))
            .map(key => {
                const parts = key.split(';');
                return {
                    fullKey: key,
                    displayName: parts[1] || parts[0]
                };
            });
        
        return keys;
    }, [selectedJobData]);

    // Set first pipe parameter as default
    useEffect(() => {
        if (pipeParameterKeys.length > 0 && !selectedPipeParam) {
            setSelectedPipeParam(pipeParameterKeys[0].fullKey);
        }
    }, [pipeParameterKeys, selectedPipeParam]);

    return (
        <div className="h-full flex flex-col">
            {/* Job Selection — only if multiple jobs. Single job info lives in modal header. */}
            {jobNumbers.length > 1 && (
                <div className="px-6 pt-4 pb-2 bg-white border-b border-[#E2E5EA]">
                    <div className="flex items-center gap-3">
                        <label className="text-[12px] font-medium text-text-grey whitespace-nowrap">
                            Select Machine/Job
                        </label>
                        <Select value={selectedJob} onValueChange={handleJobSelect}>
                            <SelectTrigger className="w-[260px] h-9">
                                <SelectValue placeholder="Choose a job number..." />
                            </SelectTrigger>
                            <SelectContent>
                                {jobNumbers.map((jobNumber) => (
                                    <SelectItem key={jobNumber} value={jobNumber}>
                                        M500: {jobNumber}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0 overflow-hidden">
                <div className="flex items-center justify-between bg-white border-b border-[#E2E5EA] px-6 flex-shrink-0">
                    <TabsList className="h-auto w-full justify-start gap-6 rounded-none bg-transparent p-0">
                        <TabsTrigger
                            value="software-versions"
                            className="rounded-none border-b-2 border-transparent px-0 py-3 text-[13px] font-medium text-heritage-grey hover:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:text-hero-red data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                        >
                            <div className="flex items-center gap-2">
                                <Code className="h-4 w-4" />
                                Software Versions
                            </div>
                        </TabsTrigger>
                        <TabsTrigger
                            value="project-information"
                            className="rounded-none border-b-2 border-transparent px-0 py-3 text-[13px] font-medium text-heritage-grey hover:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:text-hero-red data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                        >
                            <div className="flex items-center gap-2">
                                <Info className="h-4 w-4" />
                                Project Information
                            </div>
                        </TabsTrigger>
                        <TabsTrigger
                            value="job-parameters"
                            className="rounded-none border-b-2 border-transparent px-0 py-3 text-[13px] font-medium text-heritage-grey hover:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:text-hero-red data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                        >
                            <div className="flex items-center gap-2">
                                <Settings className="h-4 w-4" />
                                Job Parameters
                            </div>
                        </TabsTrigger>
                        <TabsTrigger
                            value="pipe-parameters"
                            className="rounded-none border-b-2 border-transparent px-0 py-3 text-[13px] font-medium text-heritage-grey hover:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:text-hero-red data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                        >
                            <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4" />
                                Pipe Parameters
                            </div>
                        </TabsTrigger>
                        {needsRenaming && canEdit && (
                            <TabsTrigger
                                value="rename-pass"
                                className="rounded-none border-b-2 border-transparent px-0 py-3 text-[13px] font-medium text-heritage-grey hover:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:text-hero-red data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                            >
                                <div className="flex items-center gap-2">
                                    <Edit3 className="h-4 w-4" />
                                    Rename Pass
                                </div>
                            </TabsTrigger>
                        )}
                    </TabsList>

                    {/* Pipe Parameters Dropdown — shown only when pipe-parameters tab is active */}
                    {activeTab === 'pipe-parameters' && pipeParameterKeys.length > 0 && (
                        <div className="flex-shrink-0 ml-4">
                            <Select value={selectedPipeParam} onValueChange={setSelectedPipeParam}>
                                <SelectTrigger className="w-[200px] h-9">
                                    <SelectValue placeholder="Select pipe..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {pipeParameterKeys.map((param) => (
                                        <SelectItem key={param.fullKey} value={param.fullKey}>
                                            {param.displayName}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </div>

                <div className="flex-1 min-h-0 overflow-hidden bg-page-bg">
                        <TabsContent value="software-versions" className="mt-0 h-full overflow-auto data-[state=inactive]:hidden">
                            {selectedJobData ? (
                                <SoftwareVersions jobData={selectedJobData} />
                            ) : (
                                <div className="text-center py-12 text-gray-500">
                                    <Code className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">Software Versions</h3>
                                    <p>Please select a job to view software version information</p>
                                </div>
                            )}
                        </TabsContent>

                        <TabsContent value="project-information" className="mt-0 h-full overflow-auto data-[state=inactive]:hidden">
                            {selectedJobData ? (
                                <ProjectInformation jobData={selectedJobData} />
                            ) : (
                                <div className="text-center py-12 text-gray-500">
                                    <Info className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">Project Information</h3>
                                    <p>Please select a job to view project information</p>
                                </div>
                            )}
                        </TabsContent>

                        <TabsContent value="job-parameters" className="mt-0 h-full overflow-auto data-[state=inactive]:hidden">
                            {selectedJobData ? (
                                <JobParameters jobData={selectedJobData} />
                            ) : (
                                <div className="text-center py-12 text-gray-500">
                                    <Settings className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">Job Parameters</h3>
                                    <p>Please select a job to view job parameters</p>
                                </div>
                            )}
                        </TabsContent>

                        <TabsContent value="pipe-parameters" className="mt-0 h-full flex flex-col data-[state=inactive]:hidden">
                            {selectedJobData && selectedPipeParam ? (
                                <div className="flex-1 min-h-0 flex flex-col">    
                                <PipeParametersTable 
                                    key={selectedPipeParam}
                                    jobData={selectedJobData} 
                                    pipeParamKey={selectedPipeParam}
                                />
                                </div>
                            ) : (
                                <div className="text-center py-12 text-gray-500">
                                    <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">Pipe Parameters</h3>
                                    <p>Please select a job to view pipe parameters</p>
                                </div>
                            )}
                        </TabsContent>

                        {needsRenaming && canEdit && (
                            <TabsContent value="rename-pass" className="mt-0 h-full flex flex-col overflow-hidden" >
                                {selectedJobData ? (
                                    <div className="flex-1 min-h-0 overflow-auto">   
                                    <RenamePass 
                                        jobData={selectedJobData} 
                                        projectId={projectId}
                                        onRenameSuccess={onRenameSuccess}
                                    />
                                    </div>
                                ) : (
                                    <div className="text-center py-12 text-gray-500">
                                        <Edit3 className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                                        <h3 className="text-lg font-medium text-gray-900 mb-2">Rename Pass</h3>
                                        <p>Please select a job to rename passes</p>
                                    </div>
                                )}
                            </TabsContent>
                        )}
                    </div>
                </Tabs>
    </div>
    );
};

export default M500Parameters;
